import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Choice, Label, cad, errMsg } from "@/components/sponsored/format";

const pct = (n) => `${Number(n.toFixed(2))}%`;

function SplitPreview({ base, rate, cap }) {
  if (base == null) {
    return <p className="text-sm text-slate-600" data-testid="sc-split-preview">Cyannect funds its standard base cashback (0.75%–1%); you fund the rest, up to {cad(cap)} per purchase.</p>;
  }
  const b = Number(base) * 100;
  const m = Math.max(rate - b, 0);
  return (
    <div className="rounded-2xl bg-[#E3F5EC]/70 border border-emerald-100 p-4 text-sm text-slate-700" data-testid="sc-split-preview">
      On a CA$100 repeat order the shopper gets <b>{pct(rate)}</b> (CA${rate.toFixed(2)}). Cyannect funds <b data-testid="sc-split-base">{pct(b)}</b>,
      you fund <b data-testid="sc-split-merchant">{pct(m)}</b> (CA${m.toFixed(2)}), and never more than <b>{cad(cap)}</b> per purchase.
    </div>
  );
}

export function CampaignSettingsCard({ d, onSaved }) {
  const c = d.campaign;
  const s = d.settings;
  const initial = { enabled: c.enabled, total_rate_pct: c.total_rate_pct, eligibility_days: c.eligibility_days,
    min_order_cad: c.min_order_cad || "", max_sponsored_per_purchase_cad: c.max_sponsored_per_purchase_cad };
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const capOk = Number(f.max_sponsored_per_purchase_cad) >= 1 && Number(f.max_sponsored_per_purchase_cad) <= s.max_cap_cad;
  const minOk = f.min_order_cad === "" || (Number(f.min_order_cad) >= 0 && Number(f.min_order_cad) <= s.max_min_order_cad);
  const dirty = Object.keys(initial).some((k) => String(initial[k] ?? "") !== String(f[k] ?? ""));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/merchant/sponsored-cashback/campaign", { ...f, min_order_cad: f.min_order_cad || null, expected_version: c.version });
      toast.success("Sponsored Cashback settings saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save your settings"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pf-card p-6 space-y-6" data-testid="sc-settings-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-xl font-medium text-slate-900">Offer settings</h3>
          <p className="text-sm text-slate-600 mt-1">Shoppers unlock this after their first verified sale with you, for one repeat purchase.</p>
        </div>
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-700 shrink-0">
          {f.enabled ? "On" : "Off"}
          <Switch checked={f.enabled} onCheckedChange={set("enabled")} aria-label="Sponsored Cashback offer on or off" data-testid="sc-enabled-switch" />
        </label>
      </div>
      <div className="space-y-2"><Label>Total cashback on the repeat purchase</Label>
        <Choice options={s.rate_choices} value={f.total_rate_pct} onChange={set("total_rate_pct")} suffix="%" testid="sc-rate" /></div>
      <SplitPreview base={c.base_rate} rate={f.total_rate_pct} cap={f.max_sponsored_per_purchase_cad} />
      <div className="space-y-2"><Label>Shopper has this long to come back</Label>
        <Choice options={s.window_choices} value={f.eligibility_days} onChange={set("eligibility_days")} suffix=" days" testid="sc-window" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="space-y-2 block"><Label>Minimum order (optional, CA$)</Label>
          <input className="pf-input w-full px-3 py-2 text-sm" inputMode="decimal" placeholder="No minimum" value={f.min_order_cad}
            onChange={(e) => set("min_order_cad")(e.target.value.trim())} aria-invalid={!minOk} data-testid="sc-min-order-input" />
          {!minOk && <span className="text-xs text-rose-600" data-testid="sc-min-order-error">Enter an amount from CA$0 to CA${s.max_min_order_cad.toLocaleString()}, or leave it empty.</span>}</label>
        <label className="space-y-2 block"><Label>Most you fund per purchase (CA$)</Label>
          <input className="pf-input w-full px-3 py-2 text-sm" inputMode="decimal" value={f.max_sponsored_per_purchase_cad}
            onChange={(e) => set("max_sponsored_per_purchase_cad")(e.target.value.trim())} aria-invalid={!capOk} data-testid="sc-cap-input" />
          <span className={`text-xs ${capOk ? "text-slate-500" : "text-rose-600"}`} data-testid="sc-cap-help">Between CA$1 and CA${s.max_cap_cad}.</span></label>
      </div>
      <p className="text-xs text-slate-500" data-testid="sc-reservation-note">When a shopper taps Shop Again, we set aside up to {cad(f.max_sponsored_per_purchase_cad)} from your budget for {s.reservation_days} days. Anything not used goes straight back.</p>
      <button onClick={save} disabled={busy || !capOk || !minOk || !dirty} className="pf-btn-primary px-6 py-2.5 rounded-full text-sm disabled:opacity-50" data-testid="sc-save-btn">
        {busy ? "Saving…" : dirty ? "Save settings" : "No changes to save"}
      </button>
    </section>
  );
}
